import React, { ReactElement, useEffect, useMemo, useState } from "react";

import styled from "styled-components";

import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { IconButton } from "../../components/IconButton";
import { CancelCrossIcon, CopyIcon } from "../../components/Icons";
import { InfoBox } from "../../components/InfoBox";
import { InputPassword } from "../../components/Input";
import { Label } from "../../components/Label";
import { SkeletonLoader } from "../../components/Loaders";
import { ToastMessage } from "../../components/ToastMessage";
import { Tooltip } from "../../components/Tooltip";
import { SlideFromTop } from "../../components/Transitions";
import { Body2, Caption1, Header3 } from "../../components/Typography";

/**
 * Self-hosted 2FA dialogs shared by the People stories (Users list + User
 * profile). Two flows, both reached from the user three-dot menu and both
 * gated (in the menu) behind the self-hosted + 2FA-enabled guard:
 *
 * - **Backup Codes** (own account, `self` menu): a single dialog that both
 *   explains the destructive regenerate and takes the password (identity
 *   confirmation is "valid password + submit", no separate confirm step).
 *   Submitting opens the reveal dialog, which shows a skeleton while the new
 *   set "loads", then the 10 codes once.
 * - **Create Login Code** (another user, `member`/`client` menu): an owner
 *   re-enters their own password, then a one-time code is revealed once with
 *   its expiry and a "user notified" note. (Task #18.)
 *
 * Everything is mocked locally: passwords aren't checked, the "load" is a
 * timer, and the codes are static samples.
 */

const SANS =
  '-apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif';
const MONO =
  '"SF Mono", "Roboto Mono", Menlo, Consolas, "Courier New", monospace';

/**
 * DS `InputPassword` calls `onChange` with the string value at runtime, but its
 * type is still inherited from the native input (`ChangeEventHandler`). Adapt a
 * string handler so TypeScript is satisfied without lying about the value.
 */
const asInputChange = (
  fn: (value: string) => void
): React.ChangeEventHandler<HTMLInputElement> =>
  fn as unknown as React.ChangeEventHandler<HTMLInputElement>;

/* Sample codes (10+10 A-Z0-9), mirroring the real generator's format. */
const SAMPLE_BACKUP_CODES = [
  "4K7Q9ZM2XN-8V3TJRD6WP",
  "P2W8XK4M9Q-3RJ7VND5TZ",
  "9ZM2XN4K7Q-6WP8V3TJRD",
  "TJRD6WP8V3-2XN4K7Q9ZM",
  "V3TJRD6WP8-K7Q9ZM2XN4",
  "8V3TJRD6WP-Q9ZM2XN4K7",
  "RD5TZP2W8X-M9Q3RJ7VND",
  "3RJ7VND5TZ-8XK4M9QP2W",
  "N4K7Q9ZM2X-6WP8V3TJRD",
  "Q9ZM2XN4K7-8V3TJRD6WP",
];

const SAMPLE_LOGIN_CODE = "4K7Q9ZM2XN-8V3TJRD6WP";

/* ------------------------------------------------------------------ */
/* Toast (reuses the fixed upper-right anchor pattern from headers)    */
/* ------------------------------------------------------------------ */

const StyledToastAnchor = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10000;
  pointer-events: none;

  .c-toast-message {
    pointer-events: auto;
  }
`;

interface CopyToastProps {
  open: boolean;
  toastKey: number;
  text: string;
  onClose: () => void;
}

const CopyToast = ({
  open,
  toastKey,
  text,
  onClose,
}: CopyToastProps): ReactElement => (
  <StyledToastAnchor>
    <SlideFromTop in={open}>
      <ToastMessage
        key={toastKey}
        className="c-toast-message"
        type="success"
        text={text}
        dismissible
        dropShadow
        timeout={3000}
        onClose={onClose}
      />
    </SlideFromTop>
  </StyledToastAnchor>
);

/* ------------------------------------------------------------------ */
/* Shared password-gate body (describe + password in one dialog)       */
/* ------------------------------------------------------------------ */

const StyledGateBody = styled.div`
  font-family: ${SANS};
  display: flex;
  flex-direction: column;
  gap: 16px;

  .pg-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  /* DS InputPassword wrapper is fixed-width — fill the dialog. */
  .pg-input.c-input-wrapper,
  .pg-input {
    width: 100%;
    max-width: none;
  }
`;

/* ------------------------------------------------------------------ */
/* Backup Codes — combined describe + password dialog                  */
/* ------------------------------------------------------------------ */

export interface BackupCodesDialogsProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The full Backup Codes flow: the combined regenerate/password dialog, then the
 * one-time reveal dialog with its loading skeleton. Cancelling the first dialog
 * is a pure no-op — no codes are issued, no toast.
 */
export const BackupCodesDialogs = ({
  open,
  onClose,
}: BackupCodesDialogsProps): ReactElement => {
  const [password, setPassword] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyKey, setCopyKey] = useState(0);

  // Reset the gate each time it (re)opens.
  useEffect(() => {
    if (open) {
      setPassword("");
      setAttempted(false);
    }
  }, [open]);

  // Simulate the API generating the new set: a ~1s skeleton on reveal.
  useEffect(() => {
    if (!revealOpen) {
      return undefined;
    }
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 1100);
    return () => window.clearTimeout(timer);
  }, [revealOpen]);

  const passwordInvalid = attempted && password.trim() === "";

  const handleConfirm = (): void => {
    if (password.trim() === "") {
      setAttempted(true);
      return;
    }
    // Valid password + submit is all the confirmation we need: close the gate
    // and open the reveal (which regenerates the set).
    onClose();
    setRevealOpen(true);
  };

  const handleCopy = (): void => {
    setCopyOpen(true);
    setCopyKey((k) => k + 1);
  };

  const closeReveal = (): void => {
    setRevealOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} disableCloseOnEsc>
        <Dialog.Title>Regenerate Backup Codes</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <StyledGateBody>
            <Body2 color="secondary" lineHeight="loose">
              This drops all of your existing backup codes and creates a new set
              of 10. Any codes you saved before will stop working. The new codes
              are shown only once. Enter your password to confirm it&apos;s you.
            </Body2>
            <div className="pg-field">
              <Label htmlFor="backup-codes-password">Your Password</Label>
              <InputPassword
                id="backup-codes-password"
                wrapperClassName="pg-input"
                value={password}
                onChange={asInputChange((value) => {
                  setPassword(value);
                  setAttempted(false);
                })}
                invalid={passwordInvalid}
                errorMessage={
                  passwordInvalid ? "Your password is required." : undefined
                }
                autoFocus
                autoComplete="current-password"
              />
            </div>
          </StyledGateBody>
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button
            variant="primary"
            style={{ marginRight: 12 }}
            onClick={handleConfirm}
          >
            Regenerate Codes
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Dialog.Actions>
      </Dialog>

      <BackupCodesRevealDialog
        open={revealOpen}
        loading={loading}
        onClose={closeReveal}
        onCopy={handleCopy}
      />

      <CopyToast
        open={copyOpen}
        toastKey={copyKey}
        text="Backup codes copied to clipboard."
        onClose={() => setCopyOpen(false)}
      />
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Backup Codes — one-time reveal (skeleton while loading)             */
/* ------------------------------------------------------------------ */

const StyledRevealBody = styled.div`
  font-family: ${SANS};
  display: flex;
  flex-direction: column;
  gap: 16px;

  .bc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
  }

  .bc-code {
    font-family: ${MONO};
    font-size: 14px;
    letter-spacing: 0.5px;
    color: var(--color-theme-900);
    padding: 6px 10px;
    border-radius: 6px;
    background-color: var(--color-theme-200);
    text-align: center;
  }

  .bc-skeleton {
    height: 32px;
    border-radius: 6px;
    width: 100%;
  }
`;

interface BackupCodesRevealDialogProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onCopy: () => void;
}

const BackupCodesRevealDialog = ({
  open,
  loading,
  onClose,
  onCopy,
}: BackupCodesRevealDialogProps): ReactElement => (
  <Dialog open={open} onClose={onClose} disableCloseOnEsc>
    <Dialog.Title>Your Backup Codes</Dialog.Title>
    <Dialog.ContentDivider />
    <Dialog.Content>
      <StyledRevealBody>
        <Body2 color="secondary" lineHeight="loose">
          Use a backup code to log in when email isn&apos;t available. Each code
          works once.
        </Body2>
        <div className="bc-grid">
          {loading
            ? [...Array(10)].map((_, index) => (
                <SkeletonLoader key={index} className="bc-skeleton" />
              ))
            : SAMPLE_BACKUP_CODES.map((code) => (
                <div key={code} className="bc-code">
                  {code}
                </div>
              ))}
        </div>
        <InfoBox type="note" showIcon title="Codes are shown only once">
          <Body2 color="secondary">
            Save them somewhere safe, they can&apos;t be displayed again. You
            can generate a new set from your profile at any time.
          </Body2>
        </InfoBox>
      </StyledRevealBody>
    </Dialog.Content>
    <Dialog.ContentDivider />
    <Dialog.Actions>
      <Button
        variant="primary"
        style={{ marginRight: 12 }}
        onClick={onClose}
        disabled={loading}
      >
        I&apos;ve Saved My Codes
      </Button>
      <Button variant="secondary" onClick={onCopy} disabled={loading}>
        Copy Codes
      </Button>
    </Dialog.Actions>
  </Dialog>
);

/* ------------------------------------------------------------------ */
/* Create Login Code — password gate + one-time reveal                 */
/* ------------------------------------------------------------------ */

const StyledCodeRow = styled.div`
  font-family: ${SANS};
  display: flex;
  flex-direction: column;
  gap: 16px;

  .lc-label {
    display: block;
    margin-bottom: 6px;
  }

  .lc-code-field {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    background-color: var(--color-theme-200);
  }

  .lc-code {
    font-family: ${MONO};
    font-size: 15px;
    letter-spacing: 0.5px;
    color: var(--color-theme-900);
    flex: 1 1 auto;
  }

  .lc-copy svg {
    fill: var(--color-theme-600);
  }

  .lc-caption {
    display: block;
    margin-top: 6px;
  }
`;

/* Title row for the reveal dialog: heading on the left, X close on the right
   (the app's Use Token dialog pattern). Applied via className since
   Dialog.Title doesn't accept inline style. */
const StyledRevealTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .lc-close svg {
    fill: var(--color-theme-600);
  }
`;

export interface CreateLoginCodeDialogsProps {
  open: boolean;
  onClose: () => void;
  /** Target user's full name (drives the dialog copy). */
  userName: string;
}

/**
 * Owner-minted login code flow (task #18): password gate first, then the
 * one-time reveal. The reveal has no footer — an X in the title closes it —
 * mirroring the app's Use Token dialog.
 */
export const CreateLoginCodeDialogs = ({
  open,
  onClose,
  userName,
}: CreateLoginCodeDialogsProps): ReactElement => {
  const firstName = useMemo(
    () => userName.split(" ")[0] || userName,
    [userName]
  );

  const [password, setPassword] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  // Expiry copy: computed as "now + 10 min" at the moment the code is minted.
  const [expiry, setExpiry] = useState("");

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyKey, setCopyKey] = useState(0);

  useEffect(() => {
    if (open) {
      setPassword("");
      setAttempted(false);
    }
  }, [open]);

  const passwordInvalid = attempted && password.trim() === "";

  const handleConfirm = (): void => {
    if (password.trim() === "") {
      setAttempted(true);
      return;
    }
    const when = new Date(Date.now() + 10 * 60000);
    const hh = String(when.getHours()).padStart(2, "0");
    const mm = String(when.getMinutes()).padStart(2, "0");
    setExpiry(`${hh}:${mm}`);
    onClose();
    setRevealOpen(true);
  };

  const handleCopy = (): void => {
    setCopyOpen(true);
    setCopyKey((k) => k + 1);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} disableCloseOnEsc>
        <Dialog.Title>Confirm Password</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <StyledGateBody>
            <Body2 color="secondary" lineHeight="loose">
              You&apos;re about to create a one-time login code for {userName}.
              Any code they already had pending will be replaced.
            </Body2>
            <div className="pg-field">
              <Label htmlFor="login-code-password">Your Password</Label>
              <InputPassword
                id="login-code-password"
                wrapperClassName="pg-input"
                value={password}
                onChange={asInputChange((value) => {
                  setPassword(value);
                  setAttempted(false);
                })}
                invalid={passwordInvalid}
                errorMessage={
                  passwordInvalid ? "Your password is required." : undefined
                }
                autoFocus
                autoComplete="current-password"
              />
            </div>
          </StyledGateBody>
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button
            variant="primary"
            style={{ marginRight: 12 }}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Dialog.Actions>
      </Dialog>

      <Dialog open={revealOpen} onClose={() => setRevealOpen(false)}>
        <Dialog.Title disableDefaultHeading>
          <StyledRevealTitle>
            <Header3>One-Time Login Code</Header3>
            <IconButton
              variant="text gray"
              className="lc-close"
              onClick={() => setRevealOpen(false)}
            >
              <CancelCrossIcon />
            </IconButton>
          </StyledRevealTitle>
        </Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <StyledCodeRow>
            <Body2 color="secondary" lineHeight="loose">
              Share this code with {firstName} through a secure channel.
              They&apos;ll still need their password to log in, the code alone
              won&apos;t work.
            </Body2>
            <div>
              <Label className="lc-label">Login Code</Label>
              <div className="lc-code-field">
                <span className="lc-code">{SAMPLE_LOGIN_CODE}</span>
                <Tooltip title="Copy code">
                  <IconButton
                    variant="text gray"
                    className="lc-copy"
                    onClick={handleCopy}
                  >
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </div>
              <Caption1 color="tertiary" className="lc-caption">
                Expires at {expiry} (in 10 minutes), it can&apos;t be shown
                again.
              </Caption1>
            </div>
            <InfoBox
              type="default"
              showIcon
              title={`${firstName} has been notified`}
            >
              <Body2 color="secondary">
                They received an email saying you created this code for their
                account. The email doesn&apos;t include the code.
              </Body2>
            </InfoBox>
          </StyledCodeRow>
        </Dialog.Content>
      </Dialog>

      <CopyToast
        open={copyOpen}
        toastKey={copyKey}
        text="Login code copied to clipboard."
        onClose={() => setCopyOpen(false)}
      />
    </>
  );
};
